import { Component, inject, Input, Output, EventEmitter, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GetDictionaryDTO } from 'src/app/Common/common.interface';
import { GlobalPolicyRulesService } from './global-policy-rules.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GlobalPolicyRulesCreateRequestDTO, GlobalPolicyRulesDeleteRequestDTO, GlobalPolicyRulesReadByTenantIdRequestDTO, GlobalPolicyRulesUpdateRequestDTO } from './global-policy-rules.interface';
import { ConfirmmodalserviceService } from 'src/app/Shared/confirm-delete-modal/confirmmodalservice.service';
import { ApiConfigurationDTO } from 'src/app/Common/ApiConfigurationService/api-configuration.interface';

@Component({
  selector: 'app-global-policy-rules',
  templateUrl: './global-policy-rules.component.html',
  styleUrls: ['./global-policy-rules.component.css']
})
export class GlobalPolicyRulesComponent {
  @Input() companyId: number = 0;
  @Input() branchList: GetDictionaryDTO[] = [];
  /** Set by the uiconfig "Global Policy" screen when TenantId=0/TenantBranchId=0
   *  (the platform default template) is a legitimate, explicitly selectable target —
   *  not the "nothing selected yet" placeholder that branchId===0 means everywhere else
   *  this component is embedded (company-detail, where companyId/branchId are always real). */
  @Input() allowDefaultBranch: boolean = false;
  /** Branch to preselect (e.g. restored from a `tenantBranchId` URL query param by the
   *  hosting screen) instead of always auto-selecting branchList[0]. Only applied once,
   *  the first time a matching branchList arrives. */
  @Input() initialBranchId?: number;
  /** Notifies the host whenever the selected branch changes, so it can keep a
   *  `tenantBranchId` URL query param in sync (see uiconfig's Global Policy screen). */
  @Output() branchIdChange = new EventEmitter<number>();
  /** Read-only override of the viewed tenant's own gateway URLs (Customer
   *  Details page's Global Policy Rules tab). Additive, defaults null - the
   *  uiconfig Global Policy screen (other host) never passes one. */
  @Input() apiConfigOverride: ApiConfigurationDTO | null = null;
  @Input() defaultBranchId: number = 0;
  @ViewChild('GlobalPolicyRuleForm') globalPolicyRuleFormTemplate!: TemplateRef<any>;
  @ViewChild('HowToRuleGuideModal') howToGuideModalTemplate!: TemplateRef<any>;
  howToGuideModalRef!: NgbModalRef;

  User = inject(UserLoginService).User();
  gprService = inject(GlobalPolicyRulesService);
  modalService = inject(NgbModal);
  confirmModal = inject(ConfirmmodalserviceService);

  globalPolicyRulesData: any[] = [];
  isDataLoading = false;
  isLoading = false;
  isScrolled = false;
  selectedBranchId: number = 0;
  selectedRuleCategory: string = '';  // Empty by default to show all

  ruleForm = new FormGroup({
    globalPolicyId: new FormControl(0),
    ruleCategory: new FormControl('', [Validators.required]),
    ruleName: new FormControl('', [Validators.required]),
    ruleDescription: new FormControl(''),
    ruleScript: new FormControl(''),
    otherDetail1: new FormControl(''),
    otherDetail2: new FormControl(''),
    otherDetail3: new FormControl(''),
    otherDetail4: new FormControl(''),
    otherDetail5: new FormControl(''),
    otherDetail6: new FormControl(''),
  });
  ruleModalRef!: NgbModalRef;
  isEditMode = false;

  /** Hardcoded category choices shown in the "Rule Category" dropdown.
   *  RuleCategory itself stays free text on the backend (see interface), so
   *  "Other (custom)" falls back to a plain text input for anything not listed.
   *  Most entries match POLICY_TYPE_ICONS's keys in policy-setup/policy-field-configs.ts
   *  exactly (the old hardcoded policy-type catalog's names) - picking one of these as a rule's
   *  category gets it a specific icon on its category tab in Policy Setup instead of falling
   *  back to the generic bi-sliders icon (see PolicySetupComponent.categoryIcon()). Kept to one
   *  entry per concept - e.g. no separate "Leave" and "Leave Policy" - so the list doesn't look
   *  repetitive; the "Policy"-suffixed name was kept wherever one existed since it's the one
   *  that resolves to a specific icon. */
  readonly ruleCategories: string[] = [
    'Attendance', 'Payroll', 'General',
    'Holiday Policy', 'Management Policy', 'Break Policy', 'Hourly Pay / Late Coming or Early Going Fine',
    'Basic Policy', 'Overtime Policy', 'Leave Policy', 'Compliance Policy', 'Bonus Policy',
    'Market Week Day Policy', 'Roster/Shift Policy',
  ];
  selectedCategoryOption = '';
  isCustomCategory = false;

  /** Field types a Param Definition can describe. Mirrors PolicyFieldDef['type'] in
   *  policy-setup/policy-field-configs.ts exactly — this catalog entry is consumed
   *  by policy-catalog-adapter.ts -> catalogRuleToFieldDef() and rendered generically
   *  by PolicyConfigFormComponent, so the fieldType value must match one it understands. */
  readonly fieldTypeOptions: { value: string; label: string }[] = [
    { value: 'boolean', label: 'Toggle (Yes / No)' },
    { value: 'select', label: 'Dropdown (choose one)' },
    { value: 'multiselect', label: 'Multi-select (choose many)' },
    { value: 'number', label: 'Number' },
    { value: 'duration', label: 'Duration (HH:MM)' },
    { value: 'repeatable-table', label: 'Table (repeatable rows)' },
  ];

  /** "How to configure a rule" guide content, one entry per control type, in the same order
   *  and using the same `value`s as fieldTypeOptions above - each tab in that guide modal is
   *  literally "pick the type, see how to configure it", mirroring the real Param Definition
   *  dropdown exactly instead of drifting into its own separate taxonomy (the old accordion
   *  version lumped multiselect/number/duration/table into one generic catch-all section).
   *  Step text may contain inline <strong> - authored here, never from user input, so binding
   *  it with [innerHTML] in the template is safe. */
  readonly howToGuideTabs: { value: string; label: string; badgeClass: string; whenToUse: string; steps: string[]; example: string }[] = [
    {
      value: 'boolean', label: 'Toggle', badgeClass: 'toggle',
      whenToUse: 'A plain Yes/No.',
      steps: [
        'Select <strong>Toggle (Yes / No)</strong> as the control type.',
        'Keep or edit the auto-filled Field Key.',
        'Set the default On/Off value.',
        'Optional: enable <strong>"Also collect a value with this"</strong> to pair it with a value that only appears when the toggle is On.',
        'Click <strong>Save</strong>.',
      ],
      example: '<strong>Enable Sandwich Rule</strong> (plain toggle) &nbsp;·&nbsp; <strong>Charge Late Fine</strong> paired with <strong>Minutes Late</strong> (toggle + value)',
    },
    {
      value: 'select', label: 'Dropdown', badgeClass: 'guide-badge-dropdown',
      whenToUse: 'One choice out of a fixed list.',
      steps: [
        'Select <strong>Dropdown</strong>.',
        'Add each option under Dropdown Options.',
        'Pick the Default Value.',
        'Click <strong>Save</strong>.',
      ],
      example: '<strong>Holiday Rule</strong> → Weekly Off / Paid Holiday According to Work Day / Free Holiday',
    },
    {
      value: 'multiselect', label: 'Multi-select', badgeClass: 'multi',
      whenToUse: 'More than one choice out of a fixed list.',
      steps: [
        'Select <strong>Multi-select</strong>.',
        'Add each option under Options.',
        'Check which options are selected by default.',
        'Click <strong>Save</strong>.',
      ],
      example: '<strong>ESI Calculation on</strong> → Working Salary, Holiday Salary',
    },
    {
      value: 'number', label: 'Number', badgeClass: 'number',
      whenToUse: 'A count or amount (days, minutes, rupees...).',
      steps: [
        'Select <strong>Number</strong>.',
        'Set the Default Value.',
        'Optional: add a Control Label if the number needs its own caption right next to it, e.g. "Minutes" or "Deduction as Salary of Day".',
        'Click <strong>Save</strong>.',
      ],
      example: '<strong>Half Day Hours Threshold</strong> = 150',
    },
    {
      value: 'duration', label: 'Duration', badgeClass: 'duration',
      whenToUse: 'A time of day (like shift start time) or a cutoff hour.',
      steps: [
        'Select <strong>Duration (HH:MM)</strong>.',
        'Set the Default Value in 24-hour HH:MM format.',
        'Click <strong>Save</strong>.',
      ],
      example: '<strong>In Out Roundup Timing</strong> = 00:00',
    },
    {
      value: 'repeatable-table', label: 'Table', badgeClass: 'table-type',
      whenToUse: 'Several rows of related values (tiered ranges, schedules).',
      steps: [
        'Select <strong>Table</strong>.',
        'Add each column (key, label, type).',
        'Click <strong>Save</strong> - rows themselves get added later, whenever this rule is applied to a policy.',
      ],
      example: '<strong>Overtime Time Ranges</strong> — hour thresholds mapped to a pay type/rate',
    },
  ];

  /** Column types for a repeatable-table field — matches PolicyTimeRangeColumn['type']. */
  readonly columnTypeOptions: { value: string; label: string }[] = [
    { value: 'text', label: 'Text' },
    { value: 'select', label: 'Dropdown' },
    { value: 'duration', label: 'Duration (HH:MM)' },
  ];

  /** Types a paired field can take — matches PolicyPairedField['type'] (no nested
   *  multiselect/repeatable-table; paired fields are simple companions to the main control). */
  readonly pairedTypeOptions: { value: string; label: string }[] = [
    { value: 'boolean', label: 'Toggle' },
    { value: 'select', label: 'Dropdown' },
    { value: 'number', label: 'Number' },
    { value: 'duration', label: 'Duration (HH:MM)' },
  ];

  // Param Definition builder state — replaces hand-typed JSON with structured inputs.
  paramFieldKey = '';
  paramControlLabel = '';
  paramFieldType = 'boolean';
  // Backs a select (string), a number input (number), and a duration text input (string) —
  // kept loosely typed so strictTemplates doesn't reject the number-input's numeric ngModel writes.
  paramDefaultValue: any = '';
  paramDefaultBool = false;
  paramDefaultMultiselect: string[] = [];
  paramOptions: string[] = []; // for select/multiselect
  newParamOption = '';

  // Visibility condition builder — "only show this field when another field equals a value".
  // `equals` holds the already-coerced value (boolean/number/string) so it round-trips
  // through a strict === check against the depended-upon field's real model value —
  // see coerceVisibleWhenValue().
  showVisibility = false;
  paramVisibleWhen: { field: string; equals: any }[] = [];
  newVisibleWhenField = '';
  newVisibleWhenEquals = '';

  // Paired-field builder — extra controls shown alongside the main field.
  showPaired = false;
  paramPaired: { key: string; label: string; type: string; options: string[]; default: any }[] = [];
  newPairedKey = '';
  newPairedLabel = '';
  newPairedType = 'boolean';
  newPairedOptions: string[] = [];
  newPairedOptionInput = '';
  // Backs a boolean checkbox, a text/number/duration input, and a select — loosely
  // typed for the same strictTemplates reason as paramDefaultValue above.
  newPairedDefault: any = '';

  // Repeatable-table column builder (only used when paramFieldType === 'repeatable-table').
  paramColumns: { key: string; label: string; type: string; options: string[] }[] = [];
  newColumnKey = '';
  newColumnLabel = '';
  newColumnType = 'text';
  newColumnOptions: string[] = [];
  newColumnOptionInput = '';

  pageHeader: string = 'Global Policy Rules';
  buttonHeader: string = 'Add New Rule';

  /** Every category the "Rule Category" filter can offer: the same static `ruleCategories`
   *  catalog the Create Rule modal's dropdown uses, unioned with whatever RuleCategory
   *  values are actually in use for the selected tenant/branch (RuleCategory is free text -
   *  see hrms.GlobalPolicyRules - so a tenant's custom "Other" category must still show up
   *  even though it isn't in the static catalog). Without the union, this list only ever
   *  contained categories with an existing rule, so a category with zero rules created yet
   *  (e.g. Holiday, Compliance, General) silently disappeared from the filter even though
   *  it was still pickable in the Create Rule modal - a confusing mismatch between the two
   *  dropdowns on the same screen. Refreshed whenever the unfiltered rule set is (re)loaded. */
  availableCategories: string[] = [];

  ngOnInit(): void {
    // Auto-fill the technical Field Key from Rule Name so most users never have to
    // think about it directly — still fully editable/overridable for advanced cases.
    this.ruleForm.get('ruleName')?.valueChanges.subscribe((value: string | null) => {
      if (!value || this.paramFieldKey.trim()) return;
      this.paramFieldKey = this.toCamelKey(value);
    });
  }

  toCamelKey(text: string): string {
    const words = text
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!words.length) return '';
    return words
      .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join('');
  }

  /** Consumes `initialBranchId` at most once — after the first branchList arrival, tenant
   *  switches fall back to the plain "auto-select first branch" behavior. */
  private initialBranchIdApplied = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && changes['companyId'].currentValue !== changes['companyId'].previousValue) {
      this.selectedBranchId = 0;
      this.globalPolicyRulesData = [];
      this.availableCategories = [...this.ruleCategories].sort();
    }

    if (changes['branchList'] && changes['branchList'].currentValue?.length > 0) {
      // Auto-select first branch if available, unless a not-yet-applied initialBranchId
      // (restored from a URL query param by the host) matches one in this list.
      if (!this.selectedBranchId && this.branchList.length > 0) {
        const matchesInitial = !this.initialBranchIdApplied && this.initialBranchId !== undefined
          && this.branchList.some(b => Number(b.key) === this.initialBranchId);

        this.selectedBranchId = matchesInitial ? this.initialBranchId! : Number(this.branchList[0].key);
        this.initialBranchIdApplied = true;
        this.loadGlobalPolicyRules();
        this.branchIdChange.emit(this.selectedBranchId);
      }
    }
  }

  onBranchChange(event: any): void {
    this.selectedBranchId = +event.target.value;
    this.branchIdChange.emit(this.selectedBranchId);
    if (this.selectedBranchId > 0 || this.allowDefaultBranch) {
      this.loadGlobalPolicyRules();
    } else {
      this.globalPolicyRulesData = [];
      this.availableCategories = [...this.ruleCategories].sort();
    }
  }

  /** True while a single automatic retry (see loadGlobalPolicyRules) is in flight, so the
   *  "No Records Found" empty state doesn't flash before that retry's result is known. */
  private isRetryingEmptyLoad = false;

  loadGlobalPolicyRules(isRetry: boolean = false): void {
    if (this.companyId === null || this.companyId === undefined) {
      console.warn('Company ID not available');
      return;
    }
    if (!this.allowDefaultBranch && !this.selectedBranchId) {
      console.warn('Branch ID not available');
      return;
    }

    this.isDataLoading = true;
    this.isRetryingEmptyLoad = false;
    this.globalPolicyRulesData = [];

    const data: GlobalPolicyRulesReadByTenantIdRequestDTO = {
      tenantId: this.companyId,
      tenantBranchId: this.selectedBranchId
    };

    this.gprService.GlobalPolicyRules_ReadByTenantId(data, this.apiConfigOverride?.hrms, data.tenantId, data.tenantBranchId).subscribe({
      next: (res) => {
        const items = res.items || [];

        // ReadByTenantId copies the TenantId=0/TenantBranchId=0 default rules in on the
        // very first read for a tenant/branch that has none — but the copy and this read
        // are two round trips (insert-check happens server-side, then this same call's
        // SELECT), and the very first hit for a brand-new tenant/branch has occasionally
        // come back empty even though the copy did happen (confirmed by re-running the
        // exact same read moments later and getting the copied rows). Retry once, silently,
        // before trusting an empty result — a genuinely empty tenant just re-confirms empty.
        if (items.length === 0 && !isRetry) {
          this.isRetryingEmptyLoad = true;
          setTimeout(() => this.loadGlobalPolicyRules(true), 400);
          return;
        }

        this.globalPolicyRulesData = items;
        this.availableCategories = Array.from(new Set([...this.ruleCategories, ...this.globalPolicyRulesData.map(r => r.ruleCategory)])).sort();
        this.isDataLoading = false;
      },
      error: (err) => {
        console.error('Error fetching global policy rules:', err);
        this.isDataLoading = false;
      }
    });
  }

  onRuleCategoryFilterChange(): void {
    if (this.selectedBranchId === 0 && !this.allowDefaultBranch) {
      return;
    }

    if (!this.selectedRuleCategory) {
      this.loadGlobalPolicyRules();
      return;
    }

    this.isDataLoading = true;
    this.globalPolicyRulesData = [];

    this.gprService.GlobalPolicyRules_ReadByCategory({
      ruleCategory: this.selectedRuleCategory,
      tenantId: this.companyId,
      tenantBranchId: this.selectedBranchId
    }, this.apiConfigOverride?.hrms, this.companyId, this.selectedBranchId).subscribe({
      next: (res) => {
        this.globalPolicyRulesData = res.items || [];
        this.isDataLoading = false;
      },
      error: (err) => {
        console.error('Error fetching global policy rules by category:', err);
        this.isDataLoading = false;
      }
    });
  }

  onCategorySelectChange(value: string): void {
    this.selectedCategoryOption = value;
    if (value === '__custom__') {
      this.isCustomCategory = true;
      this.ruleForm.patchValue({ ruleCategory: '' });
    } else {
      this.isCustomCategory = false;
      this.ruleForm.patchValue({ ruleCategory: value });
    }
  }

  addParamOption(): void {
    const val = this.newParamOption.trim();
    if (!val || this.paramOptions.includes(val)) return;
    this.paramOptions.push(val);
    this.newParamOption = '';
    if (this.paramFieldType === 'select' && !this.paramDefaultValue) this.paramDefaultValue = val;
  }

  removeParamOption(opt: string): void {
    this.paramOptions = this.paramOptions.filter(o => o !== opt);
    if (this.paramDefaultValue === opt) this.paramDefaultValue = this.paramOptions[0] ?? '';
    this.paramDefaultMultiselect = this.paramDefaultMultiselect.filter(o => o !== opt);
  }

  toggleMultiselectDefault(opt: string): void {
    this.paramDefaultMultiselect = this.paramDefaultMultiselect.includes(opt)
      ? this.paramDefaultMultiselect.filter(o => o !== opt)
      : [...this.paramDefaultMultiselect, opt];
  }

  /** Coerces the free-typed "Equals Value" text into the same JS type the depended-upon
   *  field's model value will actually be at render time — isVisible() in
   *  policy-config-form.component.ts compares with strict ===, so a Toggle dependency
   *  needs a real boolean here, not the string "true"/"false". */
  private coerceVisibleWhenValue(raw: string): any {
    const trimmed = raw.trim();
    if (/^(true|false)$/i.test(trimmed)) return trimmed.toLowerCase() === 'true';
    if (trimmed !== '' && !isNaN(Number(trimmed))) return Number(trimmed);
    return trimmed;
  }

  addVisibleWhen(): void {
    const field = this.newVisibleWhenField.trim();
    if (!field) return;
    this.paramVisibleWhen.push({ field, equals: this.coerceVisibleWhenValue(this.newVisibleWhenEquals) });
    this.newVisibleWhenField = '';
    this.newVisibleWhenEquals = '';
  }

  removeVisibleWhen(index: number): void {
    this.paramVisibleWhen.splice(index, 1);
  }

  addPairedOption(): void {
    const val = this.newPairedOptionInput.trim();
    if (!val || this.newPairedOptions.includes(val)) return;
    this.newPairedOptions.push(val);
    this.newPairedOptionInput = '';
  }

  removePairedOption(opt: string): void {
    this.newPairedOptions = this.newPairedOptions.filter(o => o !== opt);
  }

  addPaired(): void {
    const key = this.newPairedKey.trim();
    if (!key) return;
    const isSelect = this.newPairedType === 'select';
    this.paramPaired.push({
      key,
      label: this.newPairedLabel.trim(),
      type: this.newPairedType,
      options: isSelect ? [...this.newPairedOptions] : [],
      default: this.newPairedType === 'boolean' ? !!this.newPairedDefault
        : this.newPairedType === 'number' ? (this.newPairedDefault ? Number(this.newPairedDefault) : 0)
        : this.newPairedType === 'duration' ? (this.newPairedDefault || '00:00')
        : (this.newPairedDefault || this.newPairedOptions[0] || ''),
    });
    this.newPairedKey = '';
    this.newPairedLabel = '';
    this.newPairedType = 'boolean';
    this.newPairedOptions = [];
    this.newPairedOptionInput = '';
    this.newPairedDefault = '';
  }

  removePaired(index: number): void {
    this.paramPaired.splice(index, 1);
  }

  addColumnOption(): void {
    const val = this.newColumnOptionInput.trim();
    if (!val || this.newColumnOptions.includes(val)) return;
    this.newColumnOptions.push(val);
    this.newColumnOptionInput = '';
  }

  removeColumnOption(opt: string): void {
    this.newColumnOptions = this.newColumnOptions.filter(o => o !== opt);
  }

  addColumn(): void {
    const key = this.newColumnKey.trim();
    const label = this.newColumnLabel.trim();
    if (!key || !label) return;
    this.paramColumns.push({
      key,
      label,
      type: this.newColumnType,
      options: this.newColumnType === 'select' ? [...this.newColumnOptions] : [],
    });
    this.newColumnKey = '';
    this.newColumnLabel = '';
    this.newColumnType = 'text';
    this.newColumnOptions = [];
    this.newColumnOptionInput = '';
  }

  removeColumn(index: number): void {
    this.paramColumns.splice(index, 1);
  }

  /** Comma-joined column labels for the Param Definition preview panel - kept as a plain
   *  method since Angular template expressions can't parse an inline arrow function. */
  paramColumnsPreviewText(): string {
    return this.paramColumns.map(c => c.label || c.key).join(', ');
  }

  resetParamBuilder(): void {
    this.paramFieldKey = '';
    this.paramControlLabel = '';
    this.paramFieldType = 'boolean';
    this.paramDefaultValue = '';
    this.paramDefaultBool = false;
    this.paramDefaultMultiselect = [];
    this.paramOptions = [];
    this.newParamOption = '';

    this.showVisibility = false;
    this.paramVisibleWhen = [];
    this.newVisibleWhenField = '';
    this.newVisibleWhenEquals = '';

    this.showPaired = false;
    this.paramPaired = [];
    this.newPairedKey = '';
    this.newPairedLabel = '';
    this.newPairedType = 'boolean';
    this.newPairedOptions = [];
    this.newPairedOptionInput = '';
    this.newPairedDefault = '';

    this.paramColumns = [];
    this.newColumnKey = '';
    this.newColumnLabel = '';
    this.newColumnType = 'text';
    this.newColumnOptions = [];
    this.newColumnOptionInput = '';
  }

  /** Builds the exact CatalogParamDefinition shape policy-catalog-adapter.ts expects:
   *  {fieldKey, fieldType, options?, defaultValue?, controlLabel?, visibleWhen?, columns?, paired?}. */
  buildParamDefinitionJson(): string {
    if (!this.paramFieldKey.trim()) return '';
    const def: any = {
      fieldKey: this.paramFieldKey.trim(),
      fieldType: this.paramFieldType,
    };
    if (this.paramControlLabel.trim()) def.controlLabel = this.paramControlLabel.trim();

    switch (this.paramFieldType) {
      case 'boolean':
        def.defaultValue = this.paramDefaultBool;
        break;
      case 'select':
        def.options = this.paramOptions;
        def.defaultValue = this.paramDefaultValue || this.paramOptions[0] || '';
        break;
      case 'multiselect':
        def.options = this.paramOptions;
        def.defaultValue = this.paramDefaultMultiselect;
        break;
      case 'number':
        def.defaultValue = this.paramDefaultValue ? Number(this.paramDefaultValue) : 0;
        break;
      case 'duration':
        def.defaultValue = this.paramDefaultValue || '00:00';
        break;
      case 'repeatable-table':
        def.columns = this.paramColumns;
        def.defaultValue = [];
        break;
    }

    if (this.paramVisibleWhen.length === 1) {
      def.visibleWhen = { ...this.paramVisibleWhen[0] };
    } else if (this.paramVisibleWhen.length > 1) {
      def.visibleWhen = this.paramVisibleWhen.map(c => ({ ...c }));
    }

    if (this.paramPaired.length) {
      def.paired = this.paramPaired.map(p => ({
        key: p.key,
        label: p.label || undefined,
        fieldType: p.type,
        options: p.type === 'select' ? p.options : undefined,
        defaultValue: p.default,
      }));
    }

    return JSON.stringify(def);
  }

  /** Parses an existing rule's Param Definition JSON back into the builder fields.
   *  Legacy rows created before the builder existed (or with an unrecognized shape)
   *  just leave the builder blank rather than guessing. */
  loadParamBuilderFromJson(json: string | undefined): void {
    this.resetParamBuilder();
    if (!json) return;
    try {
      const def = JSON.parse(json);
      this.paramFieldKey = def.fieldKey ?? '';
      this.paramFieldType = def.fieldType ?? 'boolean';
      this.paramControlLabel = def.controlLabel ?? '';

      if (this.paramFieldType === 'select') {
        this.paramOptions = Array.isArray(def.options) ? def.options : [];
        this.paramDefaultValue = def.defaultValue ?? '';
      } else if (this.paramFieldType === 'multiselect') {
        this.paramOptions = Array.isArray(def.options) ? def.options : [];
        this.paramDefaultMultiselect = Array.isArray(def.defaultValue) ? def.defaultValue : [];
      } else if (this.paramFieldType === 'boolean') {
        this.paramDefaultBool = !!def.defaultValue;
      } else if (this.paramFieldType === 'duration') {
        this.paramDefaultValue = def.defaultValue ?? '00:00';
      } else if (this.paramFieldType === 'number') {
        this.paramDefaultValue = def.defaultValue != null ? String(def.defaultValue) : '';
      } else if (this.paramFieldType === 'repeatable-table') {
        this.paramColumns = Array.isArray(def.columns)
          ? def.columns.map((c: any) => ({ key: c.key, label: c.label, type: c.type, options: c.options ?? [] }))
          : [];
      }

      if (def.visibleWhen) {
        // Keep whatever type was actually stored (boolean/number/string) — re-stringifying
        // here would turn a correctly-typed `true` back into the string "true" on next save.
        this.paramVisibleWhen = Array.isArray(def.visibleWhen)
          ? def.visibleWhen.map((c: any) => ({ field: c.field, equals: c.equals }))
          : [{ field: def.visibleWhen.field, equals: def.visibleWhen.equals }];
        this.showVisibility = this.paramVisibleWhen.length > 0;
      }

      if (Array.isArray(def.paired) && def.paired.length) {
        this.paramPaired = def.paired.map((p: any) => ({
          key: p.key,
          label: p.label ?? '',
          type: p.fieldType,
          options: Array.isArray(p.options) ? p.options : [],
          default: p.defaultValue,
        }));
        this.showPaired = true;
      }
    } catch {
      // Legacy free-text paramDefinition — leave builder blank.
    }
  }

  /** Which control-type tab the "How to configure a rule" guide currently shows. Kept here
   *  (not local template state) so it resets to 'boolean' every time the modal reopens,
   *  rather than remembering whatever tab was last open. */
  howToGuideTab: string = 'boolean';

  openHowToGuide(): void {
    this.howToGuideTab = 'boolean';
    // windowClass gives this modal a stronger border/shadow (see .howto-guide-window in the
    // component CSS) so it reads as a distinct dialog instead of blending into whatever
    // modal it was opened on top of (usually the Create/Update Rule form). No `size` - the
    // old wide table-based layout needed 'lg', but the tabbed layout only shows one compact
    // type-specific panel at a time, so the default (smaller) modal width fits it better.
    this.howToGuideModalRef = this.modalService.open(this.howToGuideModalTemplate, { windowClass: 'howto-guide-window' });
  }

  addGlobalPolicyRule(): void {
    this.isEditMode = false;
    this.resetRuleForm();

    this.ruleModalRef = this.modalService.open(this.globalPolicyRuleFormTemplate, {
      size: 'xl',
      backdrop: 'static'
    });
  }

  editGlobalPolicyRule(rowData: any): void {
    this.isEditMode = true;
    this.ruleForm.patchValue({
      globalPolicyId: rowData.globalPolicyId,
      ruleCategory: rowData.ruleCategory,
      ruleName: rowData.ruleName,
      ruleDescription: rowData.ruleDescription,
      ruleScript: rowData.ruleScript,
      otherDetail1: rowData.otherDetail1,
      otherDetail2: rowData.otherDetail2,
      otherDetail3: rowData.otherDetail3,
      otherDetail4: rowData.otherDetail4,
      otherDetail5: rowData.otherDetail5,
      otherDetail6: rowData.otherDetail6,
    });

    if (rowData.ruleCategory && this.ruleCategories.includes(rowData.ruleCategory)) {
      this.selectedCategoryOption = rowData.ruleCategory;
      this.isCustomCategory = false;
    } else {
      this.selectedCategoryOption = '__custom__';
      this.isCustomCategory = true;
    }

    this.loadParamBuilderFromJson(rowData.paramDefinition);

    this.ruleModalRef = this.modalService.open(this.globalPolicyRuleFormTemplate, {
      size: 'xl',
      backdrop: 'static'
    });
  }

  deleteGlobalPolicyRule(rowData: any): void {
    this.confirmModal.openDeleteModal(rowData.ruleName!, rowData).subscribe(res => {
      if (res) {
        // rowData.tenantId/tenantBranchId are the row's actual owner, straight from the
        // loaded record — including the legitimate 0/0 of the Default Template. Substituting
        // the currently-selected UI context (or worse, the logged-in user's own company/branch)
        // here silently redirected Default Template deletes at TenantId=1/BranchId=1 instead.
        const data: GlobalPolicyRulesDeleteRequestDTO = {
          globalPolicyId: rowData.globalPolicyId,
          tenantId: rowData.tenantId,
          tenantBranchId: rowData.tenantBranchId,
          actionUser: this.User.userId.toString()
        };

        this.deleteRuleCall(data);
      }
    });
  }

  submitGlobalPolicyRule(): void {
    this.ruleForm.markAllAsTouched();

    if (this.ruleForm.valid) {
      const formValues = this.ruleForm.value;

      if (this.isEditMode && formValues.globalPolicyId && formValues.globalPolicyId !== 0) {
        const updateData: GlobalPolicyRulesUpdateRequestDTO = {
          globalPolicyId: formValues.globalPolicyId,
          tenantId: this.companyId,
          tenantBranchId: this.selectedBranchId,
          ruleCategory: formValues.ruleCategory ?? '',
          ruleName: formValues.ruleName ?? '',
          ruleDescription: formValues.ruleDescription ?? undefined,
          ruleScript: formValues.ruleScript ?? undefined,
          paramDefinition: this.buildParamDefinitionJson() || undefined,
          otherDetail1: formValues.otherDetail1 ?? undefined,
          otherDetail2: formValues.otherDetail2 ?? undefined,
          otherDetail3: formValues.otherDetail3 ?? undefined,
          otherDetail4: formValues.otherDetail4 ?? undefined,
          otherDetail5: formValues.otherDetail5 ?? undefined,
          otherDetail6: formValues.otherDetail6 ?? undefined,
          isActive: 1,
          actionUser: this.User.userId.toString()
        };

        this.updateGlobalPolicyRule(updateData);
      } else {
        const createData: GlobalPolicyRulesCreateRequestDTO = {
          tenantId: this.companyId,
          tenantBranchId: this.selectedBranchId,
          ruleCategory: formValues.ruleCategory ?? '',
          ruleName: formValues.ruleName ?? '',
          ruleDescription: formValues.ruleDescription ?? undefined,
          ruleScript: formValues.ruleScript ?? undefined,
          paramDefinition: this.buildParamDefinitionJson() || undefined,
          otherDetail1: formValues.otherDetail1 ?? undefined,
          otherDetail2: formValues.otherDetail2 ?? undefined,
          otherDetail3: formValues.otherDetail3 ?? undefined,
          otherDetail4: formValues.otherDetail4 ?? undefined,
          otherDetail5: formValues.otherDetail5 ?? undefined,
          otherDetail6: formValues.otherDetail6 ?? undefined,
          actionUser: this.User.userId.toString()
        };

        this.createGlobalPolicyRule(createData);
      }
    }
  }

  createGlobalPolicyRule(data: GlobalPolicyRulesCreateRequestDTO): void {
    this.isLoading = true;

    this.gprService.GlobalPolicyRules_Create(data).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.ruleModalRef.close();
        this.loadGlobalPolicyRules();
      },
      error: (err) => {
        console.error('Error creating global policy rule:', err);
        this.isLoading = false;
      }
    });
  }

  updateGlobalPolicyRule(data: GlobalPolicyRulesUpdateRequestDTO): void {
    this.isLoading = true;

    this.gprService.GlobalPolicyRules_Update(data).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.ruleModalRef.close();
        this.loadGlobalPolicyRules();
      },
      error: (err) => {
        console.error('Error updating global policy rule:', err);
        this.isLoading = false;
      }
    });
  }

  deleteRuleCall(data: GlobalPolicyRulesDeleteRequestDTO): void {
    this.gprService.GlobalPolicyRules_Delete(data).subscribe({
      next: (res) => {
        this.loadGlobalPolicyRules();
      },
      error: (err) => {
        console.error('Error deleting global policy rule:', err);
      }
    });
  }

  resetRuleForm(): void {
    this.isEditMode = false;
    this.ruleForm.reset({
      globalPolicyId: 0,
      ruleCategory: '',
      ruleName: '',
      ruleDescription: '',
      ruleScript: '',
      otherDetail1: '',
      otherDetail2: '',
      otherDetail3: '',
      otherDetail4: '',
      otherDetail5: '',
      otherDetail6: '',
    });
    this.selectedCategoryOption = '';
    this.isCustomCategory = false;
    this.resetParamBuilder();
  }

  onScroll(event: any): void {
    this.isScrolled = event.target.scrollTop > 0;
  }

  /** "fieldKey (Field Type label)" for the grid's Field Key / Type column, parsed
   *  from paramDefinition JSON. Legacy/free-text paramDefinition rows show '—'. */
  getParamFieldDisplay(rowData: any): string {
    if (!rowData?.paramDefinition) return '—';
    try {
      const def = JSON.parse(rowData.paramDefinition);
      if (!def.fieldKey) return '—';
      const typeLabel = this.fieldTypeOptions.find(f => f.value === def.fieldType)?.label ?? def.fieldType;
      return `${def.fieldKey} (${typeLabel})`;
    } catch {
      return '—';
    }
  }
}
